import { useMemo } from "react";
import type {
  CheckpointData,
  ConstrainedAllocationData,
  RestrictionAdjustmentData,
  RowClass,
  SecondarySecuritiesData,
  StopReason,
  TaxConstraintsData,
} from "../lib/explainTypes";
import { ISSUE_STOP_REASONS } from "../lib/explainTypes";
import TaxConstraintsSection from "./TaxConstraintsSection";
import ConstrainOverview from "./ConstrainOverview";
import SymbolConstraintTable from "./SymbolConstraintTable";
import WaterfallSection from "./WaterfallSection";

interface Props {
  constrained: ConstrainedAllocationData | null;
  taxConstraints: TaxConstraintsData | null;
  isTaxable: boolean;
  modelCheckpoint: CheckpointData | undefined;
  restrictions: RestrictionAdjustmentData | null;
  secondary: SecondarySecuritiesData | null;
}

const ISSUE_SET = new Set<StopReason>(ISSUE_STOP_REASONS);

// Reads `account.constrained_allocation`. Replaces the legacy
// Tabs (Adjustments / Allocation / Lots) with a stacked narrative
// layout per dc-constrain-section-ui-redesign-replace-019e1dc7:
//   Header strip   (Lot selection | Tax constraints panel)
//   Overview chips (lots, symbols, ST/LT realized, Issues, By-design)
//   Overweight table  (only rows where the constrain pass acted)
//   Underweight/at-target table (same active-engagement filter)
//
// "Active engagement" means: this symbol has a sold lot, a
// restriction (hold_only / sell_no_tax / exclude / like_primary), a
// substitution, or an adjustment with an issue-class stop_reason.
// Passive residual_redeploy rows are dropped — those are bookkeeping
// from other symbols' caps and don't represent a constraint event of
// their own. Per-symbol buy-side narrative lives in the Trades
// section.
export default function ConstrainSection({
  constrained,
  taxConstraints,
  isTaxable,
  modelCheckpoint,
  restrictions,
  secondary,
}: Props) {
  const modelTarget = modelCheckpoint?.by_symbol ?? {};
  const restrictionEntries = useMemo(
    () => restrictions?.restrictions ?? [],
    [restrictions],
  );
  const substitutionEntries = useMemo(
    () => secondary?.substitutions ?? [],
    [secondary],
  );

  const groups = useMemo(() => {
    if (!constrained) {
      return { overweight: [] as string[], underweight: [] as string[] };
    }
    const rowClasses = constrained.row_classes ?? {};

    const soldSymbols = new Set(
      constrained.lot_adjustments
        .map((l) => l.symbol)
        .filter((s): s is string => !!s),
    );
    const restrictedSymbols = new Set(restrictionEntries.map((r) => r.symbol));
    const substitutedSymbols = new Set<string>();
    for (const s of substitutionEntries) {
      if (s.primary) substitutedSymbols.add(s.primary);
      if (s.secondary) substitutedSymbols.add(s.secondary);
    }
    const cappedSymbols = new Set(
      constrained.adjustments
        .filter((a) => a.stop_reason && ISSUE_SET.has(a.stop_reason))
        .map((a) => a.symbol),
    );

    const isActive = (sym: string): boolean =>
      soldSymbols.has(sym) ||
      restrictedSymbols.has(sym) ||
      substitutedSymbols.has(sym) ||
      cappedSymbols.has(sym);

    const overweight: string[] = [];
    const underweight: string[] = [];

    for (const [symbol, klass] of Object.entries(rowClasses) as Array<
      [string, RowClass]
    >) {
      if (!isActive(symbol)) continue;
      if (klass === "overweight") {
        overweight.push(symbol);
      } else {
        underweight.push(symbol);
      }
    }

    overweight.sort();
    underweight.sort();
    return { overweight, underweight };
  }, [constrained, restrictionEntries, substitutionEntries]);

  if (!constrained) {
    return <div className="text-muted">No constrained allocation.</div>;
  }

  const lotSelection =
    isTaxable && taxConstraints ? taxConstraints.lot_selection_method : null;

  return (
    <>
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <strong>Lot selection:</strong>{" "}
          {lotSelection ?? <span className="text-muted">—</span>}
        </div>
        <div style={{ maxWidth: "50%" }}>
          <TaxConstraintsSection
            taxConstraints={taxConstraints}
            isTaxable={isTaxable}
          />
        </div>
      </div>

      <ConstrainOverview constrained={constrained} isTaxable={isTaxable} />

      <SymbolConstraintTable
        title="Overweight"
        symbols={groups.overweight}
        currentWeights={constrained.current_weights ?? {}}
        modelTarget={modelTarget}
        finalTarget={constrained.final_target}
        adjustments={constrained.adjustments}
        lotAdjustments={constrained.lot_adjustments}
        restrictions={restrictionEntries}
        substitutions={substitutionEntries}
        isTaxable={isTaxable}
      />

      <SymbolConstraintTable
        title="Underweight / at target"
        symbols={groups.underweight}
        currentWeights={constrained.current_weights ?? {}}
        modelTarget={modelTarget}
        finalTarget={constrained.final_target}
        adjustments={constrained.adjustments}
        lotAdjustments={constrained.lot_adjustments}
        restrictions={restrictionEntries}
        substitutions={substitutionEntries}
        isTaxable={isTaxable}
      />

      {constrained.allocation_tree &&
        constrained.allocation_tree.length > 0 && (
          <WaterfallSection
            tree={constrained.allocation_tree}
            substitutions={substitutionEntries}
          />
        )}
    </>
  );
}
